/**
 * Google Tag Manager Utility (ohne direktes GA4 gtag.js Snippet).
 * Erwartet process.env.googleTagManagerId als Objekt:
 *   { de: 'GTM-XXXXXXX', at: 'GTM-YYYYYYY', ... }
 *
 * Nutzung (client side):
 *   const gtm = new GoogleTagManager(getCountryCode(true), { autoPageView: true })
 *   gtm.sendPageView('Titel', '/pfad')
 *   gtm.pushEvent('add_to_cart', { sku, price })
 *   gtm.setUserId('123')
 *   gtm.setConsent({ ad_storage: 'denied', analytics_storage: 'granted' })
 */

declare const window: any

export interface GTMOptions {
  dataLayerName?: string // Standard: dataLayer
  autoPageView?: boolean // initialer page_view nach Init
  debug?: boolean // zusätzliche console.debug Ausgaben
  consentDefaults?: Record<string, 'granted' | 'denied'> // Anfangs-Consent
  loadStrategy?: 'head' | 'firstScript' // Einfüge-Strategie für <script>
  enabledInDev?: boolean // auch in development aktiv
}

/* eslint-disable camelcase */
interface PageViewPayload {
  event: string
  page_title?: string
  page_path?: string
  page_location?: string
  [k: string]: any
}
/* eslint-enable camelcase */

export class GoogleTagManager {
  private containerId: string | null = null
  private options: GTMOptions
  private initialized = false
  private scriptInjected = false
  private queue: any[] = []
  private dlName: string

  constructor(domain: string, options: GTMOptions = {}) {
    this.options = {
      dataLayerName: 'dataLayer',
      autoPageView: false,
      debug: false,
      consentDefaults: undefined,
      loadStrategy: 'head',
      enabledInDev: false,
      ...options,
    }
    this.dlName = this.options.dataLayerName as string
    if (this.isBrowser()) {
      if (document.cookie.includes('debugTracking=yes')) {
        this.options.debug = true
      }

      if (this.options.debug) {
        console.debug('[GTM] constructor start ' + domain)
      }
      this.bootstrap(domain)
    }
  }

  // ---------------- Public API ----------------

  sendPageView(title?: string, path?: string) {
    if (!this.ready()) {
      this.enqueue(() => this.sendPageView(title, path))
      return
    }
    const pv: PageViewPayload = {
      event: 'page_view',
      page_title: title || document.title,
      page_path: path || window.location.pathname,
      page_location: window.location.href,
    }
    this.push(pv)
    this.debug('page_view', pv)

    if (this.options.debug) {
      console.debug('[GTM] page_view pushed', pv)
    }
  }

  pushEvent(eventName: string, data: Record<string, any> = {}) {
    if (!this.ready()) {
      this.enqueue(() => this.pushEvent(eventName, data))
      return
    }
    const payload = { event: eventName, ...data }
    this.push(payload)
    this.debug('event', payload)
  }

  setUserId(userId: string) {
    if (!this.ready()) {
      this.enqueue(() => this.setUserId(userId))
      return
    }
    // Persistente Variable + Event
    this.push({
      event: 'user_id_set',
      user_id: userId,
    })
    this.debug('user_id_set', { user_id: userId })
  }

  /**
   * Consent Update (ohne gtag, rein dataLayer basiert).
   * Beispiel: setConsent({ ad_storage: 'denied', analytics_storage: 'granted' })
   */
  setConsent(consents: { [k: string]: 'granted' | 'denied' }) {
    if (!this.ready()) {
      this.enqueue(() => this.setConsent(consents))
      return
    }
    this.push({
      event: 'consent_update',
      ...consents,
    })
    this.debug('consent_update', consents)
  }

  // ---------------- Internal ----------------

  private bootstrap(domain: string) {
    this.containerId = this.resolveContainerId(domain)
    if (!this.containerId) {
      if (this.options.debug) {
        console.debug('[GTM] no tag id for country', domain)
      }
      return
    }
    if (!this.isActive()) {
      this.debug('GTM deactivated (Environment)')
      if (this.options.debug) {
        console.debug('[GTM] inactive due to environment')
      }
      return
    }
    this.ensureDataLayer()
    this.initialDataLayerPush()
    this.injectScript()
    this.initialized = true

    if (this.options.debug) {
      console.debug('[GTM] initialized', { id: this.containerId })
    }

    if (this.options.consentDefaults) {
      this.setConsent(this.options.consentDefaults)
    }

    if (this.options.autoPageView) {
      this.sendPageView()
    }

    // Flush Queue
    const q = [...this.queue]
    this.queue = []
    q.forEach((fn) => {
      try {
        fn()
      } catch (e) {
        this.debug('Queue Fehler', e)
      }
    })

    this.debug('GTM init success', { id: this.containerId })
  }

  private resolveContainerId(domain: string): string | null {
    const cfg: any = process.env.googleTagManagerId
    if (!cfg) return null
    if (typeof cfg === 'string') {
      // Single container across all domains
      return cfg.startsWith('GTM-') ? cfg : null
    }
    if (typeof cfg === 'object') {
      if (domain in cfg) {
        const id = cfg[domain]
        return typeof id === 'string' && id.startsWith('GTM-') ? id : null
      }
    }
    return null
  }

  private isActive(): boolean {
    if (this.options.enabledInDev) return true
    return process.env.NODE_ENV === 'production'
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined'
  }

  private ensureDataLayer() {
    window[this.dlName] = window[this.dlName] || []
  }

  private initialDataLayerPush() {
    this.push({
      'gtm.start': Date.now(),
      event: 'gtm.js',
    })
  }

  private injectScript() {
    if (this.scriptInjected || !this.containerId) return
    const id = this.containerId
    const dlParam =
      this.dlName !== 'dataLayer' ? `&l=${encodeURIComponent(this.dlName)}` : ''
    const script = document.createElement('script')
    script.id = 'gtm-script'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
      id
    )}${dlParam}`

    if (this.options.loadStrategy === 'firstScript') {
      const ref = document.getElementsByTagName('script')[0]
      ref?.parentNode?.insertBefore(script, ref)
    } else {
      document.head.appendChild(script)
    }
    this.scriptInjected = true
    if (this.options.debug) {
      console.debug('[GTM] script injected', {
        id: this.containerId,
        dl: this.dlName,
        src: script.src,
      })
    }
  }

  private push(obj: Record<string, any>) {
    window[this.dlName].push(obj)
  }

  private enqueue(fn: () => void) {
    this.queue.push(fn)
  }

  private ready(): boolean {
    return this.initialized && !!this.containerId
  }

  private debug(label: string, data?: any) {
    if (this.options.debug) {
      console.debug('[GTM]', label, data || '')
    }
  }
}
